import { useEffect, useMemo, useState } from 'react';
import { Alert, App, Form, Input, Modal, Typography } from 'antd';

import { correctItem } from '../../services/records';
import { getItemDisplayValue } from '../../utils/items';

/**
 * Correcting a record of record: a NEW entry that restates the original,
 * with both kept and the pairing explicit.
 *
 * The modal asks for two things and says why. The reason is required
 * because a correction whose reason is blank tells a later reader that
 * something changed and nothing about why — the half that matters. The
 * changed values are optional: a correction is usually one field, and the
 * API carries everything else over rather than making the client resend a
 * whole record it might drop a field from.
 *
 * Deliberately NOT an edit form: this modal takes the fields the person
 * wants to restate, and the item form stays the place where an OPEN record
 * is edited. Mixing the two would blur the one distinction the feature
 * exists to draw.
 */

const { Text } = Typography;

export default function ItemCorrectionModal({ open, item, card, elementDefs = [], onClose, onCorrected }) {

	const { message } = App.useApp();
	const [ reason, setReason ] = useState('');
	const [ values, setValues ] = useState({});
	const [ saving, setSaving ] = useState(false);

	useEffect(() => {
		if (!open) return;
		setReason('');
		setValues({});
	}, [ open, item?._id ]);

	const title = useMemo(
		() => getItemDisplayValue({ card, elementDefs, item }),
		[ card, elementDefs, item ]
	);

	// Text-shaped elements only: the correction modal is a small, typed-by-
	// hand restatement, and every other type is better corrected by opening
	// the correction itself once it exists.
	const editable = useMemo(
		() => (elementDefs || []).filter(el => el?.name && !el?.options?.drivenFrom
			&& [ 'text-small', 'text-large', 'choice', 'number', 'integer', 'decimal' ].includes(el.dataType)),
		[ elementDefs ]
	);

	const submit = async () => {
		if (!reason.trim()) {
			message.error('A correction needs a reason.');
			return;
		}
		setSaving(true);
		try {
			const result = await correctItem(item._id, {
				reason: reason.trim(),
				elements: Object.keys(values).length ? values : undefined,
			});
			message.success('Correction recorded. The original is kept beside it.');
			onCorrected?.(result);
			onClose?.();
		}
		catch (err) {
			message.error(err?.response?.data?.message || 'Could not record the correction.');
		}
		finally {
			setSaving(false);
		}
	};

	return (
		<Modal
			open={open}
			onCancel={onClose}
			onOk={submit}
			okText='Record the correction'
			confirmLoading={saving}
			title={`Correct “${ title }”`}
			destroyOnClose
		>
			<Alert
				type='info'
				showIcon
				style={{ marginBottom: 12 }}
				message='The original is kept'
				description='A correction is a new entry that supersedes this one. Both stay readable, and the pairing says which restates which.'
			/>

			<Form layout='vertical'>
				<Form.Item label='What changed, and why' required>
					<Input.TextArea
						rows={3}
						value={reason}
						autoFocus
						aria-label='What changed, and why'
						placeholder='Recorded against the wrong resident — this is the corrected entry.'
						onChange={event => setReason(event.target.value)}
					/>
				</Form.Item>

				{editable.map(el => (
					<Form.Item key={el.name} label={el.label?.singular || el.name}>
						<Input
							value={values[ el.name ] ?? (item?.elements?.[ el.name ] ?? '')}
							aria-label={`Corrected ${ el.label?.singular || el.name }`}
							onChange={event => setValues(prev => ({ ...prev, [ el.name ]: event.target.value }))}
						/>
					</Form.Item>
				))}
				<Text type='secondary'>Anything you leave alone is carried over unchanged.</Text>
			</Form>
		</Modal>
	);
}
